/**
 * AuthContext
 *
 * Owns the current user's identity for the whole React tree. Three states:
 *   • `loading` (initial mount — we're checking /api/auth/me)
 *   • `user = object` (authenticated via JumpCloud OIDC)
 *   • `user = null` (not authenticated → ProtectedRoute renders Login)
 *
 * The only sign-in path is JumpCloud SSO (see /api/auth/jumpcloud/start).
 * Local password login and the preview bypass have been removed.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { authLogout, authMe, getMyAccess, magicLogout } from "@/lib/api";
import { setTheme } from "@/lib/theme";
import { fieldSessionReady, getFieldToken, clearFieldToken } from "@/lib/fieldSession";
import { setCurrentUserId, clearForLogout } from "@/lib/offlineQueue";
import { setCurrentUserEmail } from "@/lib/currentUser";

const AuthContext = createContext(null);

// Offline resilience: the last authenticated identity is cached so a
// technician who reopens the app with no signal (basement, canyon,
// after a phone reboot) still gets the shell instead of the login page.
// This only affects RENDERING — every API call still carries the
// httpOnly cookie and the backend enforces RBAC, so a cached identity
// grants no access on its own.
const LAST_USER_KEY = "abs_last_user";
const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(LAST_USER_KEY) || "null"); }
  catch { return null; }
};
const writeCachedUser = (u) => {
  try {
    if (u) localStorage.setItem(LAST_USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(LAST_USER_KEY);
  } catch { /* private mode */ }
};
// A failed /auth/me with no HTTP response = the network, not the session.
const isNetworkError = (e) =>
  !e?.response || (typeof navigator !== "undefined" && navigator.onLine === false);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter268 — effective access matrix (surface id → none/read/write).
  // Server-enforced too; this only drives what the UI offers.
  const [access, setAccess] = useState(null);
  // Named admins (email allowlist) are the only ones who may edit the
  // permission matrix or touch Admin accounts.
  const [isNamedAdmin, setIsNamedAdmin] = useState(false);
  const themeAppliedFor = useRef(null);

  const refresh = useCallback(async () => {
    try {
      await fieldSessionReady;
      const { data } = await authMe();
      setUser(data);
      writeCachedUser(data);
      // Theme default: the user's saved preference, else dark. Applied once per sign-in so a session toggle sticks.
      if (data?.id && themeAppliedFor.current !== data.id) {
        themeAppliedFor.current = data.id;
        setTheme(data.theme === "light" ? "light" : "dark");
      }
      // #126 (Group G): tag the offline queue with the current user
      // so any subsequent enqueue / flush is scoped to this session.
      setCurrentUserId(data?.id || null);
      // #(med) (iter168): sync the JWT-derived email into the legacy
      // `abs_user_email` key so currentUser.js fallback reads stay
      // authoritative (no more localStorage spoofing surface).
      setCurrentUserEmail(data?.email || null);
      // iter237 — expose the logged-in user's display name so proposal
      // creation can default the AM ("prepared_by") to the creator.
      window.__ABS_USER_NAME = data?.name || "";
      try {
        const res = await getMyAccess();
        setAccess(res.data?.surfaces || {});
        setIsNamedAdmin(!!res.data?.is_named_admin);
      } catch { setAccess({}); }
    } catch (e) {
      const cached = isNetworkError(e) ? readCachedUser() : null;
      if (cached) {
        // Offline — keep working with the last known identity.
        setUser(cached);
        setCurrentUserId(cached.id || null);
        setCurrentUserEmail(cached.email || null);
        window.__ABS_USER_NAME = cached.name || "";
      } else {
        setUser(null);
        writeCachedUser(null);
        setCurrentUserId(null);
        setCurrentUserEmail(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // axios interceptor dispatches this when refresh-token fails so any
    // tab in the SPA can react (clearing user state → ProtectedRoute
    // bounces to /login).
    const onExpired = () => {
      setUser(null);
      writeCachedUser(null);
      setCurrentUserId(null);
      setCurrentUserEmail(null);
    };
    window.addEventListener("auth:expired", onExpired);
    // Permissions edited in Settings → Users should reach an open session
    // without a re-login: re-read the matrix whenever the tab regains focus.
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      getMyAccess().then(res => {
        setAccess(res.data?.surfaces || {});
        setIsNamedAdmin(!!res.data?.is_named_admin);
      }).catch(() => {});
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    const tick = setInterval(onFocus, 60_000);
    return () => {
      window.removeEventListener("auth:expired", onExpired);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      clearInterval(tick);
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    if (getFieldToken()) {
      try { await magicLogout(); } catch { /* ignore */ }
      await clearFieldToken();
    }
    try { await authLogout(); } catch { /* ignore */ }
    // #126 (Group G): drop the offline queue on logout so the next
    // user on this device cannot fire pings under a stale identity.
    try { await clearForLogout(); } catch { /* ignore */ }
    // #235 (iter168): clear the legacy currentUser.js identity keys
    // (`abs_user_email`, `abs_current_user`, …) so the next sign-in
    // doesn't see the previous user's email/name. The JWT cookie is
    // already cleared server-side by authLogout, but those localStorage
    // values are read directly by some pages (WorkOrderTab, Proposals,
    // Leads) for autofill/audit fields.
    setCurrentUserEmail(null);
    setUser(null);
    writeCachedUser(null);
    themeAppliedFor.current = null;
  }, []);

  // can("settings.users", "read" | "write"). Defaults to permissive while
  // the matrix is still loading so nothing flickers away mid-render.
  const can = useCallback((surfaceId, level = "read") => {
    if (!access) return true;
    const order = { none: 0, read: 1, write: 2 };
    return (order[access[surfaceId]] || 0) >= order[level];
  }, [access]);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh, access, can, isNamedAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
